import React from 'react';
import { Link, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart } from '@fortawesome/pro-light-svg-icons';
import { faShoppingBag } from '@fortawesome/pro-regular-svg-icons';
import { faTrash, faHeart as faHeartSolid, faCheck, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { confirmAlert } from 'react-confirm-alert';
import icons from '../../static/icons';
import cn from 'classnames';
import './CuratorCardNew.scss';

import { getLinkToShopForUserId, getLinkToShopForUsername, getCategoryById, getDepartmentById, getIndustryById } from '../../Helpers/shop_helpers';
import { dontBubble } from '../../Helpers/helpers';
import { isCuratorIdFavoritedSomewhere } from '../../Helpers/user_helpers';
import { split_db_social_links_into_individual_links } from '../../Helpers/social_helpers';

import ConfirmDelete from '../General/ConfirmDelete';
import SmartImage from '../General/SmartImage';

const CuratorCard = props => {
  const { shop, groupUser, curator, isEditing, activeFilters, isCuratedByMe } = props;

  // Pull out the User_id and image
  let User_id, image, name, username, social_links;
  if (groupUser) {
    User_id = groupUser.User_id;
    image = groupUser.user.image;
    name = groupUser.user.name;
    username = groupUser.user.username;
    social_links = groupUser.user.social_links;
  } else if (curator) {
    User_id = curator.id;
    image = curator.image;
    name = curator.name;
    username = curator.username;
    social_links = curator.social_links;
  }

  // Content
  // Curated By Message
  let curatedByMessage;
  if (activeFilters?.Category_id) {
    curatedByMessage = `${getCategoryById(shop, activeFilters.Category_id)?.name}, curated by`;
  } else if (activeFilters?.Department_id) {
    curatedByMessage = `${getDepartmentById(shop, activeFilters.Department_id)?.name}, curated by`;
  } else if (activeFilters?.Industry_id) {
    curatedByMessage = `${getIndustryById(shop, activeFilters.Industry_id)?.name}, curated by`;
  } else {
    curatedByMessage = 'Curated by';
  }

  const { instagram_url, tiktok_url, youtube_url } = split_db_social_links_into_individual_links(social_links);
  const showSocialIcons = !!instagram_url || !!tiktok_url || !!youtube_url;

  // Allow Deleting if this is your list
  const removeGroupUser = e => {
    e.preventDefault();
    e.stopPropagation();
    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmDelete
          header='Are you sure?'
          subheader={`This will permanently remove ${name} from this group.`}
          onCancel={onClose}
          hideCancel={false}
          onDelete={() => {
            props.deleteCuratorGroupUser(groupUser);
            onClose();
          }}
        />
      )
    });
  };

  // Allow Favoriting if this is not your list
  const isFavorite = isCuratorIdFavoritedSomewhere(props.user, User_id);
  const canClickFavorite = !isCuratedByMe && props.openCuratorGroupModal;
  const clickFavorite = e => {
    dontBubble(e);
    props.openCuratorGroupModal({
      params: {
        type: 'add',
        curator: {
          id: User_id,
          name: name,
          image: image
        }
      }
    });
  };

  // Links
  const linkToShop = username
    ? getLinkToShopForUsername(username, props.activeFilters)
    : User_id && getLinkToShopForUserId(User_id, props.activeFilters);
  const goToShop = e => {
    dontBubble(e);
    props.history.push(linkToShop);
  };

  // Actions
  const additionalClasses = {
    'on-dark': props.onDark,
    'is-loading': props.isLoading,
    selected: props.isSelected,
    selectable: props.isSelectable,
    'another-selected': props.isAnotherSelected
  };
  return props.isEmpty ? (
    <div className={cn('new-curator-card-container empty', additionalClasses)}>
      <div className={cn('image-container empty', additionalClasses)}>
        <div className='empty-image empty' />
      </div>
      <div className={cn('data-container empty', additionalClasses)}>
        <div className='name empty'>Loading...</div>
        <div className={cn('icons', additionalClasses)}>
          <div className='social-icon empty' />
          <div className='social-icon empty' />
        </div>
      </div>
    </div>
  ) : (
    <div className={cn('new-curator-card-container', additionalClasses)}>
      {props.isSelectable ? (
        <div className={cn('background-image', additionalClasses)}>
          {image ? (
            <SmartImage fadeInDelay={200} src={image} alt={name} fallbackText={name} loading={props.lazyLoadImages ? 'lazy' : 'eager'} />
          ) : (
            <div className='empty-image' />
          )}
        </div>
      ) : (
        <Link to={linkToShop} className={cn('background-image', additionalClasses)}>
          {image ? (
            <SmartImage fadeInDelay={200} src={image} alt={name} fallbackText={name} loading={props.lazyLoadImages ? 'lazy' : 'eager'} />
          ) : (
            <div className='empty-image' />
          )}
        </Link>
      )}
      <div onClick={dontBubble} className={cn('actions', additionalClasses)}>
        {!props.hideActions && (
          <>
            {!isEditing && !props.isSelectable && (
              <div onClick={goToShop} className={cn('action shop show-action-on-card-hover', additionalClasses)}>
                <FontAwesomeIcon icon={faShoppingBag} />
                <div className='explainer'>Shop</div>
              </div>
            )}
            {canClickFavorite && !props.isSelectable && (
              <div className='action favorite show-action-on-card-hover' onClick={clickFavorite}>
                <FontAwesomeIcon icon={isFavorite ? faHeartSolid : faHeart} />
                <div className='explainer'>Favorite</div>
              </div>
            )}
            {isEditing && (
              <div onClick={removeGroupUser} className={cn('action delete', additionalClasses)}>
                <FontAwesomeIcon icon={faTrash} />
              </div>
            )}
            {isEditing && (
              <div className={cn('action reorder', additionalClasses)}>
                {props.rearrangeHandle}
                <div className='explainer'>Rearrange</div>
              </div>
            )}
            {props.isSelectable && (
              <div onClick={props.onToggle} className={cn('action select', additionalClasses)}>
                <FontAwesomeIcon icon={props.isSelected ? faCheck : faPlus} />
                <div className='explainer'>Select</div>
              </div>
            )}
          </>
        )}
      </div>
      <div className={cn('content', additionalClasses)}>
        {props.isSelectable ? (
          <div onClick={props.onToggle} className='data-container'>
            <div className='curated-by'>{curatedByMessage}</div>
            <div className='title'>{name}</div>
          </div>
        ) : props.isLoading ? (
          <div className='data-container'>
            <div className='curated-by'>{curatedByMessage}</div>
            <div className='title'>{name}</div>
          </div>
        ) : (
          <Link to={linkToShop} className='data-container'>
            <div className='curated-by'>{curatedByMessage}</div>
            <div className='title'>{name}</div>
          </Link>
        )}
        {showSocialIcons && !props.isComingSoon && (
          <div className={cn('icons', additionalClasses)}>
            {instagram_url && (
              <a href={instagram_url} target='_blank' rel='noopener noreferrer' className='social-icon'>
                <img src={icons.social.instagram.white} alt='Instagram' />
              </a>
            )}
            {tiktok_url && (
              <a href={tiktok_url} target='_blank' rel='noopener noreferrer' className='social-icon'>
                <img src={icons.social.tiktok.white} alt='TikTok' />
              </a>
            )}
            {youtube_url && (
              <a href={youtube_url} target='_blank' rel='noopener noreferrer' className='social-icon'>
                <img src={icons.social.youtube.white} alt='YouTube' />
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

CuratorCard.propTypes = {
  user: PropTypes.object.isRequired,
  isEditing: PropTypes.bool,

  // Group User (from list), Curator (from APIs), or Empty
  groupUser: PropTypes.object,
  curator: PropTypes.object,
  isEmpty: PropTypes.bool,

  // Optional if we want to open them to a certain page
  activeFilters: PropTypes.object,

  // UI
  onDark: PropTypes.bool,
  hideActions: PropTypes.bool,
  isLoading: PropTypes.bool,
  lazyLoadImages: PropTypes.bool,
  isComingSoon: PropTypes.bool,

  // If Selectable
  isSelectable: PropTypes.bool,
  isSelected: PropTypes.bool,
  isAnotherSelected: PropTypes.bool,
  onToggle: PropTypes.func,

  // If Reordable
  rearrangeHandle: PropTypes.object,

  // Actions
  deleteCuratorGroupUser: PropTypes.func,
  openCuratorGroupModal: PropTypes.func // If you want to allow them to heart
};

export default withRouter(CuratorCard);
